import type { ReactNode } from 'react';
import { forwardRef } from 'react';

import { ImageUpload } from '@shared/core-ui-image-upload';
import { georgeStats, trackEvent2 } from '@shared/statistics';
import { CustomizationService } from '@api-client/products';
import { dataURLtoBlob } from '@shared/image';
import { getFeatureConfig } from '@luna/feature-config';

import type { ActivePAPIProductWithAvatarData } from '../types';
import { useActivePAPIProduct } from '../../../ActivePAPIProductContext';
import { useActiveProductServices } from '../../../ActiveProductServicesContext';
import { gAPIAccountEntitySlice } from '../../../gAPIAccountEntitySlice';
import { invalidateProductImage } from '../../../papi-migration/hooks/useProductImage';
import { removeImageFromCache, saveImageToCache } from '../../../services/imageLoadingService';

type Props = {
    dropdownToggle?: ReactNode;
    productType?: string;
} & Parameters<typeof trackEvent2<'productCustomisationChanges', 'productImageChangeStarted'>>[2];

const IMAGE_PREFIX_LENGTH = 22;

export const ProductImageUpload = forwardRef<HTMLInputElement, Props>(
    ({ dropdownToggle, trigger, productType = '' }, ref) => {
        const product = useActivePAPIProduct<ActivePAPIProductWithAvatarData>();
        const { updateCache } = useActiveProductServices();

        const {
            id,
            localizedName,
            _embedded: {
                customization: { image },
            },
        } = product;
        const findLegacyProduct = () =>
            gAPIAccountEntitySlice.selectAll().find(({ productId }) => productId === id);
        const shouldUpdateLegacyProduct = () =>
            getFeatureConfig(({ products }) => products.relyOnGAPIForAggregation) &&
            !!findLegacyProduct();

        const handleImageUpload = (imageUrl: string) =>
            CustomizationService.postPublicProductsCustomizationImage({
                id,
                formData: { file: dataURLtoBlob(imageUrl) },
            }).then(() => {
                saveImageToCache(id, imageUrl.substr(IMAGE_PREFIX_LENGTH));
                updateCache({
                    ...product,
                    _embedded: {
                        ...product._embedded,
                        customization: {
                            ...product._embedded.customization,
                            image: ['ORIGINAL', 'V250x250'],
                        },
                    },
                });

                if (shouldUpdateLegacyProduct()) {
                    const latestLegacyProduct = findLegacyProduct();

                    if (latestLegacyProduct) {
                        saveImageToCache(
                            latestLegacyProduct.id,
                            imageUrl.substr(IMAGE_PREFIX_LENGTH),
                        );
                        gAPIAccountEntitySlice.updateCache({
                            ...latestLegacyProduct,
                            hasImage: true,
                        });
                    }
                }
            });

        const handleImageCustomizationStart = () =>
            trackEvent2('productCustomisationChanges', 'productImageChangeStarted', {
                trigger,
            });

        const handleImageCustomizationSuccess = () =>
            trackEvent2('productCustomisationChanges', 'productCustomised', {
                productType,
                customisationChanges: ['name'],
            });

        const handleUploadedImageChange = () => {
            georgeStats.trackEvent('accountDetails', 'accountAvatarChanged', {
                productI18N: localizedName,
            });
            handleImageCustomizationSuccess();
        };

        const handleImageRemove = async () => {
            handleImageCustomizationStart();
            return CustomizationService.deletePublicProductsCustomizationImage({ id }).then(() => {
                invalidateProductImage(id);
                updateCache({
                    ...product,
                    _embedded: {
                        ...product._embedded,
                        customization: {
                            ...product._embedded.customization,
                            image: [],
                        },
                    },
                });
                removeImageFromCache(id);

                if (shouldUpdateLegacyProduct()) {
                    const latestLegacyProduct = findLegacyProduct();

                    if (latestLegacyProduct) {
                        removeImageFromCache(latestLegacyProduct.id);
                        gAPIAccountEntitySlice.updateCache({
                            ...latestLegacyProduct,
                            hasImage: false,
                        });
                    }
                }
                handleImageCustomizationSuccess();
            });
        };

        return (
            <ImageUpload
                ref={ref}
                onUploadedImageChange={handleUploadedImageChange}
                onImageUpload={handleImageUpload}
                onImageRemove={handleImageRemove}
                disableRemoveImage={!image?.length}
                showResultAsNotification
                dropdownToggle={dropdownToggle}
                onChangeImageClick={handleImageCustomizationStart}
            />
        );
    },
);
